import React, { createContext, useContext, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/react-router';
import { apiClient, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';

// Retry configuration for handling backend token sync delays
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 2000,
};

// Type for our authenticated API client
export interface AuthenticatedApiClient {
  get<T = unknown>(endpoint: string, params?: Record<string, string>): Promise<T>;
  post<T = unknown>(endpoint: string, data?: unknown): Promise<T>;
  patch<T = unknown>(endpoint: string, data?: unknown): Promise<T>;
  delete<T = unknown>(endpoint: string): Promise<T>;
  getToken: () => Promise<string | null>;
}

// Context value type - includes both API client and readiness state
interface AuthenticatedApiContextValue {
  api: AuthenticatedApiClient;
  isReady: boolean;
}

// Create the context
const AuthenticatedApiContext = createContext<AuthenticatedApiContextValue | null>(null);

// Loading state component shown while auth initializes
function AuthLoadingState() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <Skeleton className="h-12 w-12 rounded-full mx-auto" />
        <Skeleton className="h-4 w-32 mx-auto" />
      </div>
    </div>
  );
}

// Helper to retry API calls on 401 errors (handles backend token sync delays)
async function withAuthRetry<T>(
  operation: () => Promise<T>,
  getNewToken: () => Promise<string | null>,
  retryCount = 0
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    // Only retry on 401 errors within retry limits
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      retryCount < RETRY_CONFIG.maxRetries
    ) {
      const delay = Math.min(
        RETRY_CONFIG.baseDelayMs * Math.pow(2, retryCount),
        RETRY_CONFIG.maxDelayMs
      );
      console.log(`[Auth] 401 received, retrying in ${delay}ms (attempt ${retryCount + 1}/${RETRY_CONFIG.maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));

      // Get a fresh token before retrying
      await getNewToken();

      return withAuthRetry(operation, getNewToken, retryCount + 1);
    }
    throw error;
  }
}

// Provider component
export function AuthenticatedApiProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const tokenVerifiedRef = useRef(false);

  // Create a stable getToken wrapper that doesn't need polling
  // since we verify the token before setting isReady
  const stableGetToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getToken();
      if (token) {
        return token;
      }
      console.warn('[Auth] stableGetToken returned null - user may not be signed in');
      return null;
    } catch (error) {
      console.error('[Auth] stableGetToken failed:', error);
      return null;
    }
  }, [getToken, isLoaded, isSignedIn]);

  // Create an authenticated API client with retry logic for 401s
  const authenticatedApi = useMemo<AuthenticatedApiClient>(() => {
    return {
      get: async <T = unknown>(endpoint: string, params?: Record<string, string>) => {
        return withAuthRetry(
          async () => {
            const token = await stableGetToken();
            return apiClient.get<T>(endpoint, params, token);
          },
          stableGetToken
        );
      },
      post: async <T = unknown>(endpoint: string, data?: unknown) => {
        return withAuthRetry(
          async () => {
            const token = await stableGetToken();
            return apiClient.post<T>(endpoint, data, token);
          },
          stableGetToken
        );
      },
      patch: async <T = unknown>(endpoint: string, data?: unknown) => {
        return withAuthRetry(
          async () => {
            const token = await stableGetToken();
            return apiClient.patch<T>(endpoint, data, token);
          },
          stableGetToken
        );
      },
      delete: async <T = unknown>(endpoint: string) => {
        return withAuthRetry(
          async () => {
            const token = await stableGetToken();
            return apiClient.delete<T>(endpoint, token);
          },
          stableGetToken
        );
      },
      getToken: stableGetToken,
    };
  }, [stableGetToken]);

  // Verify token is available before marking as ready
  useEffect(() => {
    // If Clerk hasn't loaded yet, wait
    if (!isLoaded) {
      setIsReady(false);
      tokenVerifiedRef.current = false;
      return;
    }

    // If user is not signed in, we're "ready" but with no valid token
    // This allows unauthenticated routes (like sign-in) to render
    if (!isSignedIn) {
      setIsReady(true); // Allow rendering, but API calls will fail (as expected)
      tokenVerifiedRef.current = false;
      return;
    }

    // Already verified in this session
    if (tokenVerifiedRef.current) {
      return;
    }

    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 30; // 30 * 100ms = 3 seconds max

    const verifyToken = async () => {
      try {
        const token = await getToken();
        if (cancelled) return;

        if (token) {
          console.log('[Auth] Token verified, waiting for backend sync...');
          // Add a small delay to allow Clerk's backend to sync the session
          // This helps prevent 401s immediately after sign-in
          await new Promise(resolve => setTimeout(resolve, 500));
          console.log('[Auth] Auth ready');
          tokenVerifiedRef.current = true;
          setIsReady(true);
        } else if (retryCount < maxRetries) {
          // Token not yet available, retry
          retryCount++;
          setTimeout(verifyToken, 100);
        } else {
          console.error('[Auth] Failed to verify token after max retries');
          // Still set ready to avoid infinite loading - API calls will fail gracefully
          setIsReady(true);
        }
      } catch (error) {
        if (cancelled) return;

        if (retryCount < maxRetries) {
          console.warn('[Auth] Token verification failed, retrying...', error);
          retryCount++;
          setTimeout(verifyToken, 100);
        } else {
          console.error('[Auth] Failed to verify token after max retries:', error);
          setIsReady(true);
        }
      }
    };

    verifyToken();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  // Set the singleton instance when ready (only if signed in)
  useEffect(() => {
    if (isReady && isSignedIn) {
      setAuthenticatedApiInstance(authenticatedApi);
    }
  }, [isReady, isSignedIn, authenticatedApi]);

  // Show loading only when:
  // 1. Clerk hasn't loaded yet, OR
  // 2. User is signed in but token isn't verified yet
  if (!isLoaded || (isSignedIn && !isReady)) {
    return <AuthLoadingState />;
  }

  return (
    <AuthenticatedApiContext.Provider value={{ api: authenticatedApi, isReady }}>
      {children}
    </AuthenticatedApiContext.Provider>
  );
}

// Hook to use the authenticated API (backwards compatible)
export function useAuthenticatedApi(): AuthenticatedApiClient {
  const context = useContext(AuthenticatedApiContext);

  if (!context) {
    throw new Error('useAuthenticatedApi must be used within AuthenticatedApiProvider');
  }

  return context.api;
}

// Hook to check auth readiness state
export function useAuthState(): { isReady: boolean } {
  const context = useContext(AuthenticatedApiContext);

  if (!context) {
    throw new Error('useAuthState must be used within AuthenticatedApiProvider');
  }

  return { isReady: context.isReady };
}

// Export a singleton instance for services that need direct access
// This will be set by the provider and used by services
let authenticatedApiInstance: AuthenticatedApiClient | null = null;

export function setAuthenticatedApiInstance(instance: AuthenticatedApiClient) {
  authenticatedApiInstance = instance;
}

export function getAuthenticatedApi(): AuthenticatedApiClient {
  if (!authenticatedApiInstance) {
    throw new Error('Authenticated API not initialized. Make sure AuthenticatedApiProvider is mounted.');
  }
  return authenticatedApiInstance;
}
